import { useMemo } from 'react';
import type { FinancialParams } from '../utils/financialDefaults';
import { deliveredEnergy } from '../utils/financialCalculations';
import type { SystemConfig, SimulationResults, FinancialKpis, CapexBreakdown, YearCashflow } from '../utils/financialCalculations';

export default function ConfigSummaryBar({ sys, sim, params, degradation, kpis, capex, cashflows, profileDefaults, onResetToDefaults }: {
  sys: SystemConfig; sim: SimulationResults; params: FinancialParams; degradation: number;
  kpis: FinancialKpis; capex: CapexBreakdown; cashflows: YearCashflow[];
  profileDefaults: { pv: number; bE: number; bP: number; eff: number; dod: number; buP: number; gMax: number; dem: number; deg: number };
  onResetToDefaults?: () => void;
}) {
  const deviations = useMemo(() => {
    const checks = [
      { label: 'PV', current: sys.pvCapacity, default_: profileDefaults.pv, unit: 'MWp' },
      { label: 'BESS', current: sys.bessEnergy, default_: profileDefaults.bE, unit: 'MWh' },
      { label: 'Backup', current: sys.backupPower, default_: profileDefaults.buP, unit: 'MW' },
      { label: 'Grid', current: sys.gridMax, default_: profileDefaults.gMax, unit: 'MW' },
    ];
    return checks.filter(c => Math.abs(c.current - c.default_) > 0.001);
  }, [sys, profileDefaults]);

  const fingerprint = useMemo(() => [sys.pvCapacity.toFixed(1), sys.bessEnergy.toFixed(1), sys.bessPower.toFixed(1), sys.gridMax.toFixed(0), params.chargingTariff.toFixed(2), params.wacc.toFixed(1), `${params.projectDuration}y`, degradation.toFixed(1)].join('-'), [sys, params, degradation]);

  const checks = useMemo(() => {
    const results: { label: string; ok: boolean; detail: string }[] = [];
    const capexSum = capex.pv + capex.bess + capex.backup + capex.chargers + capex.gridConnection + capex.engineering;
    results.push({ label: 'CAPEX som', ok: Math.abs(capex.total - capexSum) < 1, detail: `€${(capex.total / 1e6).toFixed(2)}M` });
    const npvFromCf = cashflows.reduce((sum, cf) => cf.year === 0 ? sum + cf.netCashflow : sum + cf.netCashflow / Math.pow(1 + params.wacc / 100, cf.year), 0);
    results.push({ label: 'NPV', ok: Math.abs(kpis.npv - npvFromCf) < 100, detail: `€${(kpis.npv / 1e6).toFixed(2)}M` });
    const y1 = cashflows.find(cf => cf.year === 1);
    results.push({ label: 'Revenue ≥ 0', ok: !y1 || y1.totalRevenue >= 0, detail: y1 ? `€${(y1.totalRevenue / 1000).toFixed(0)}k` : 'n/a' });
    return results;
  }, [capex, cashflows, kpis, params.wacc]);

  const allOk = checks.every(c => c.ok);

  // Omzetbasis in beeld op de pagina waar de bedragen staan. `sim.totalDelivered`
  // is de VRAAG; alleen `deliveredEnergy(sim)` wordt gefactureerd. Deze balk zat
  // al op beide businesscase-pagina's maar liet het verschil niet zien: op de
  // Wognum-invoer (pv 2 MWp, 20 MWh/10 MW BESS, 0,33 MW aansluiting, geen
  // aggregaat, 19.000 MWh, jaar 15) is dat 4.149,9 geleverd van 18.994,3 gevraagd
  // — 14.844,4 MWh (78,2 %) waar geen euro tegenover staat.
  const delivered = deliveredEnergy(sim);
  const unmetPct = sim.totalDelivered > 0 ? (sim.unmet / sim.totalDelivered) * 100 : 0;
  const unmetIsMaterial = Number.isFinite(unmetPct) && unmetPct >= 1;
  return (
    <div className="mb-3 px-4 py-2.5 bg-white rounded-lg border border-[#D9E2EC] shadow-sm text-[10px] space-y-1.5">
      {deviations.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-amber-600 font-semibold">⚠️ Geladen uit simulatie</span>
          <span className="text-[#8899AA]">({deviations.map(d => `${d.label}: ${d.current} ${d.unit}`).join(', ')})</span>
          {onResetToDefaults && <button onClick={onResetToDefaults} className="px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 cursor-pointer font-semibold transition-colors">Reset naar defaults</button>}
        </div>
      )}
      <div className="flex items-center gap-4 flex-wrap">
        <span className={unmetIsMaterial ? 'text-red-600 font-semibold' : 'text-[#8899AA]'}>
          ⚡ Omzetbasis: <strong>{delivered.toFixed(0)} MWh</strong> geleverd van {sim.totalDelivered.toFixed(0)} MWh gevraagd
          {unmetIsMaterial && ` — ${sim.unmet.toFixed(0)} MWh (${unmetPct.toFixed(1)}%) ongedekt, niet factureerbaar`}
        </span>
        <span className="text-[#8899AA]">🔑 <span className="font-mono">{fingerprint}</span></span>
        <span className={allOk ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold'}>{allOk ? '✅ Integriteit OK' : '❌ Integriteitscheck'}</span>
      </div>
    </div>
  );
}
